import { createForm } from "@tanstack/solid-form";
import { type UseQueryResult, useQuery } from "@tanstack/solid-query";
import {
	batch,
	type Component,
	createContext,
	createEffect,
	createMemo,
	createSignal,
	useContext
} from "solid-js";
import { PaymentMethodMethods, PaymentMethodModel } from "~/solidJs/entities/PaymentMethod";
import { getPublicMessageFromError } from "~/solidJs/shared";
import { PaymentApi, PaymentSystem } from "~/solidJs/shared/api/PaymentApi";
import {
	type CalculateAddMembersCostResponse,
	DurationMonths,
	TeamApi,
	type TeamPricingResponse
} from "~/solidJs/shared/api/TeamApi";
import { showToastMessage, ToastMessageTypes } from "~/solidJs/widgets/WorkspaceToast";

export interface TeamSeatsModalState {
	seats: () => string;
	teamName: () => string;
	teamPricingQuery: () => UseQueryResult<
		TeamPricingResponse | CalculateAddMembersCostResponse,
		Error
	>;
	getSelectedPaymentMethod: () => PaymentMethodModel.PaymentMethod[];
	getSelectedPaymentMethodWithoutNew: () => PaymentMethodModel.PaymentMethod | null;
	selectedCurrency: () => PaymentMethodModel.PaymentCurrency;
	setSelectedCurrency: (currency: PaymentMethodModel.PaymentCurrency) => void;
	setSelectPaymentMethod: (method: PaymentMethodModel.PaymentMethod) => void;
	transformedPaymentMethod: () => PaymentMethodModel.PaymentMethod[];
	getSelectedCurrency: () => string;
	durationMonths: () => DurationMonths;
	setDurationMonths: (duration: DurationMonths) => void;
	isAddingSeats: boolean;
}

export interface TeamSeatsModalConfig {
	title: string;
	subtitle: string;
	minSeats: number;
	getPromoMessage: (state: TeamSeatsModalState) => string | null;
	showContactSales: (state: TeamSeatsModalState) => boolean;
	onPayment: (state: TeamSeatsModalState) => Promise<void>;
	onSuccess?: () => void;
	onClose: () => void;
	FormInputs: Component;
	teamName?: string;
	buttonText?: string;
	showBillingPlanSelector?: boolean;
	isAddingSeats?: boolean;
	currentTeamSize?: number;
	isReactivating?: boolean;
	initialDuration?: DurationMonths;
}

interface _FormValues {
	teamName: string;
	seats: string;
}

export const TeamSeatsModalContext = createContext<ReturnType<typeof useTeamSeatsModal>>();

export const useTeamSeatsModal = (config: TeamSeatsModalConfig) => {
	const [getSelectedPaymentMethod, setSelectedPaymentMethod] = createSignal<
		PaymentMethodModel.PaymentMethod[]
	>([]);
	const [selectedCurrency, setSelectedCurrency] =
		createSignal<PaymentMethodModel.PaymentCurrency>(
			PaymentMethodModel.PRICING_PAGE_CURRENCIES[0]
		);
	const [durationMonths, setDurationMonths] = createSignal<DurationMonths>(
		config.initialDuration || DurationMonths.Yearly
	);

	// Флаг для предотвращения запросов до полной инициализации
	const [isInitialized, setIsInitialized] = createSignal(false);

	// Форма для управления teamName и seats
	const Form = createForm(() => ({
		defaultValues: {
			teamName: config.teamName || "",
			seats: config.isAddingSeats ? "1" : config.minSeats.toString()
		},
		async onSubmit(formState) {
			const state: TeamSeatsModalState = {
				seats: () => formState.value.seats,
				teamName: () => formState.value.teamName,
				teamPricingQuery,
				getSelectedPaymentMethod: getSelectedPaymentMethod,
				selectedCurrency: selectedCurrency,
				setSelectedCurrency,
				setSelectPaymentMethod: setSelectPaymentMethodHandler,
				transformedPaymentMethod,
				getSelectedCurrency: getSelectedCurrency,
				getSelectedPaymentMethodWithoutNew,
				durationMonths,
				setDurationMonths,
				isAddingSeats: config.isAddingSeats || false
			};
			try {
				await config.onPayment(state);
				config.onSuccess?.();
			} catch (error) {
				showToastMessage({
					type: ToastMessageTypes.Error,
					title: getPublicMessageFromError(error) || "Unknown error occurred"
				});
			}
		}
	}));

	const paymentMethodsQuery = useQuery(() => PaymentApi.getSavedPaymentMethodsOptions());

	// Инициализация payment method только один раз когда данные загрузились
	createEffect(() => {
		// Пропускаем если уже выбран метод
		if (getSelectedPaymentMethod().length) return;

		// Ждем пока данные загрузятся
		const methods = paymentMethodsQuery.data;
		if (!methods) return;

		// Используем batch для группировки обновлений и предотвращения множественных запросов
		batch(() => {
			if (methods.length > 0) {
				// Find default method or use first one
				const savedMethod = methods.find((method) => method.is_default) || methods[0];
				const transformed = PaymentMethodMethods.transformPaymentMethod(savedMethod);
				setSelectedPaymentMethod([transformed]);
				// Set currency based on the payment method
				const newCurrency =
					transformed.system === PaymentSystem.Stripe
						? PaymentMethodModel.UsdCurrencyPricing
						: PaymentMethodModel.RubCurrencyPricing;
				setSelectedCurrency(newCurrency);
			} else {
				// No saved methods - use "Add new payment method"
				setSelectedPaymentMethod([PaymentMethodMethods.createAddNewPaymentMethodItem()]);
			}
			// Помечаем как инициализированное только после установки всех значений
			setIsInitialized(true);
		});
	});
	// Реактивный доступ к значениям формы
	const seats = Form.useStore((state) => state.values.seats);
	const teamName = Form.useStore((state) => state.values.teamName);
	const isSubmitting = Form.useStore((state) => state.isSubmitting);

	// Мемоизированный расчет итогового количества сидов
	const totalSeats = createMemo(() => {
		const parsedSeats = Number.parseInt(seats(), 10) || config.minSeats;
		if (config.isAddingSeats && config.currentTeamSize) {
			return config.currentTeamSize + parsedSeats;
		}
		return parsedSeats;
	});

	// Показывать ли BillingPlanSelector (по умолчанию true для обратной совместимости)
	const showBillingSelector = config.showBillingPlanSelector !== false;

	// Мемоизируем параметры для запросов, чтобы избежать лишних перезапросов
	const yearlyPricingParams = createMemo(
		():
			| { isAddingSeats: true; seatsToAdd: number }
			| {
					isAddingSeats: false;
					seats: number;
					paymentMethodId: string | null;
					currency: string;
			  } => {
			if (config.isAddingSeats) {
				const seatsToAdd = Number.parseInt(seats(), 10) || 1;
				return {
					isAddingSeats: true as const,
					seatsToAdd
				};
			}
			return {
				isAddingSeats: false as const,
				seats: totalSeats(),
				paymentMethodId: getSelectedPaymentMethod()[0]?.id || null,
				currency: selectedCurrency().value
			};
		}
	);

	// Query для yearly pricing (активен если показываем selector ИЛИ выбран yearly)
	const yearlyPricingQuery = useQuery<TeamPricingResponse | CalculateAddMembersCostResponse>(
		() => {
			const params = yearlyPricingParams();

			// Для добавления seat'ов используем другой эндпоинт
			if (params.isAddingSeats) {
				const options = TeamApi.calculateAddMembersCostOptions({
					new_seats_subscriptions: [
						{
							plan_name: "teams",
							quantity: params.seatsToAdd
						}
					]
				});
				return {
					...options,
					// Для добавления seat'ов запрос всегда активен после инициализации
					enabled: isInitialized()
				} as any;
			}

			// Для создания новой команды используем старый эндпоинт
			const options = TeamApi.calculateTeamPricingOptions({
				seats: params.seats,
				payment_method_id: params.paymentMethodId,
				currency: params.currency,
				plan: "teams",
				duration_months: DurationMonths.Yearly
			});
			return {
				...options,
				// Запрос активен только после инициализации И если нужен
				enabled:
					isInitialized() &&
					(showBillingSelector || durationMonths() === DurationMonths.Yearly)
			};
		}
	);

	const monthlyPricingParams = createMemo(
		():
			| { isAddingSeats: true }
			| {
					isAddingSeats: false;
					seats: number;
					paymentMethodId: string | null;
					currency: string;
			  } => {
			if (config.isAddingSeats) {
				return { isAddingSeats: true as const };
			}
			return {
				isAddingSeats: false as const,
				seats: totalSeats(),
				paymentMethodId: getSelectedPaymentMethod()[0]?.id || null,
				currency: selectedCurrency().value
			};
		}
	);

	// Query для monthly pricing (активен если показываем selector ИЛИ выбран monthly)
	const monthlyPricingQuery = useQuery<TeamPricingResponse>(() => {
		const params = monthlyPricingParams();

		// Для добавления seat'ов не нужен monthly query
		if (params.isAddingSeats) {
			return {
				queryKey: ["disabled"],
				queryFn: () => Promise.resolve({} as TeamPricingResponse),
				enabled: false
			} as any;
		}

		// Для создания новой команды используем старый эндпоинт
		const options = TeamApi.calculateTeamPricingOptions({
			seats: params.seats,
			payment_method_id: params.paymentMethodId,
			currency: params.currency,
			plan: "teams",
			duration_months: DurationMonths.Monthly
		});
		return {
			...options,
			// Запрос активен только после инициализации И если нужен
			enabled:
				isInitialized() &&
				(showBillingSelector || durationMonths() === DurationMonths.Monthly)
		};
	});

	// Просто мемоизированная ссылка на нужный query
	const teamPricingQuery = createMemo(() =>
		durationMonths() === DurationMonths.Yearly ? yearlyPricingQuery : monthlyPricingQuery
	);

	// Мемоизируем трансформированные payment methods
	const transformedPaymentMethod = createMemo(() => {
		const methods = paymentMethodsQuery.data;
		if (!methods) {
			return [PaymentMethodMethods.createAddNewPaymentMethodItem()];
		}

		const transformed = methods.map(PaymentMethodMethods.transformPaymentMethod);

		// Для "Add seats": если есть хотя бы один Stripe метод, не показываем "Add new payment method"
		if (config.isAddingSeats) {
			const hasStripeMethod = methods.some((m) => m.system === PaymentSystem.Stripe);
			if (hasStripeMethod) {
				return transformed; // Только сохраненные методы, без "Add new"
			}
		}

		return [PaymentMethodMethods.createAddNewPaymentMethodItem(), ...transformed];
	});

	const setSelectPaymentMethodHandler = (paymentMethod: PaymentMethodModel.PaymentMethod) => {
		// Используем batch чтобы избежать двойного запроса при изменении метода оплаты и валюты
		batch(() => {
			setSelectedPaymentMethod([paymentMethod]);
			// Update currency based on payment method system
			if (paymentMethod.id !== PaymentMethodModel.ADD_NEW_PAYMENT_METHOD_ID) {
				const newCurrency =
					paymentMethod.system === PaymentSystem.Stripe
						? PaymentMethodModel.UsdCurrencyPricing
						: PaymentMethodModel.RubCurrencyPricing;
				setSelectedCurrency(newCurrency);
			}
		});
	};

	const handleCurrencyChange = (currency: PaymentMethodModel.PaymentCurrency) => {
		// Currency can only be changed when "Add new payment method" is selected
		setSelectedCurrency(currency);
	};

	const getSelectedCurrency = () => {
		return selectedCurrency().value;
	};

	const getSelectedPaymentMethodWithoutNew = () => {
		const method = getSelectedPaymentMethod()[0];
		if (method && method.id !== PaymentMethodModel.ADD_NEW_PAYMENT_METHOD_ID) {
			return method;
		}
		return null;
	};

	return {
		// Form
		Form,

		// Reactive form values
		seats,
		teamName,

		// State
		teamPricingQuery,
		yearlyPricingQuery,
		monthlyPricingQuery,
		paymentMethodsQuery,
		getSelectedPaymentMethod: getSelectedPaymentMethod,
		getSelectedPaymentMethodWithoutNew,
		selectedCurrency,
		durationMonths,

		// Methods
		transformedPaymentMethod,
		setSelectedCurrency: handleCurrencyChange,
		setSelectPaymentMethod: setSelectPaymentMethodHandler,
		setDurationMonths,
		getSelectedCurrency,
		isSubmitting,

		// Config
		config
	};
};

export const useTeamSeatsModalContext = () => {
	const context = useContext(TeamSeatsModalContext);
	if (!context) {
		throw new Error(
			"useTeamSeatsModalContext must be used within TeamSeatsModalContext.Provider"
		);
	}
	return context;
};
